import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getStudentDetail, type StudentDetail } from '../../lib/adminApi';
import { formatDate, formatDateTime, timeAgo } from '../../lib/format';
import { buildOutline } from '../../lib/progress';
import { friendlyError } from '../../lib/supabase';
import { Badge, Callout, ProgressBar, Spinner, StatTile } from '../../components/ui';

export function AdminStudentDetail() {
  const { userId = '' } = useParams();
  const [data, setData] = useState<StudentDetail | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { getStudentDetail(userId).then(setData).catch((e) => setError(friendlyError(e))); }, [userId]);

  if (error) return <Callout variant="error">{error}</Callout>;
  if (data === undefined) return <Spinner />;
  if (data === null) return <Callout variant="error">Student not found, or they aren’t in a course you can view.</Callout>;

  const { profile, enrollments, courses, chapters, lessons, progress } = data;
  const totalDone = enrollments.reduce((s, e) => s + e.completed_lessons, 0);

  return (
    <>
      <div className="small muted"><Link to="/admin/students">Students</Link> / {profile.full_name || profile.email}</div>
      <header>
        <h1 style={{ margin: 0, fontSize: 28, lineHeight: '36px', fontWeight: 400, color: 'var(--brand)' }}>{profile.full_name || 'Unnamed student'}</h1>
        <p className="muted" style={{ margin: '4px 0 0' }}>
          {profile.email}{profile.job_title ? ` · ${profile.job_title}` : ''}{profile.organization ? ` · ${profile.organization}` : ''}
        </p>
      </header>

      <section className="stats-grid" aria-label="Activity summary">
        <StatTile label="Last sign-in" value={timeAgo(profile.last_sign_in_at)} hint={formatDateTime(profile.last_sign_in_at)} />
        <StatTile label="Account created" value={formatDate(profile.created_at_auth)} />
        <StatTile label="Courses" value={enrollments.length} hint={`${enrollments.filter((e) => e.status === 'completed').length} completed`} />
        <StatTile label="Lessons completed" value={totalDone} />
      </section>

      {enrollments.length === 0 && <div className="empty">Not enrolled in any course yet. Enroll them from the Students page.</div>}

      {enrollments.map((en) => {
        const course = courses.find((c) => c.id === en.course_id);
        if (!course) return null;
        const prog = progress.filter((p) => p.enrollment_id === en.enrollment_id);
        const outline = buildOutline(chapters.filter((c) => c.course_id === course.id), lessons.filter((l) => l.course_id === course.id), prog,
          { sequential: course.sequential, enrolled: true });
        const byLesson = new Map(prog.map((p) => [p.lesson_id, p]));
        const lastLesson = lessons.find((l) => l.id === en.last_lesson_id);
        return (
          <section key={en.enrollment_id} className="box stack" aria-labelledby={`c-${course.id}`}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <h2 id={`c-${course.id}`} style={{ margin: 0, fontSize: 20, lineHeight: '28px', fontWeight: 600, color: 'var(--brand)' }}>{course.title}</h2>
              {en.status === 'completed' ? <Badge tone="success">Completed {formatDate(en.completed_at)}</Badge>
                : en.status === 'revoked' ? <Badge tone="danger">Access removed</Badge>
                : en.completed_lessons === 0 ? <Badge>Not started</Badge> : <Badge tone="brand">In progress</Badge>}
            </div>
            <ProgressBar value={Number(en.percent_complete)} label={`${en.completed_lessons} of ${en.required_lessons} required lessons`} />
            <div className="form-grid small" style={{ gap: 8 }}>
              <span><span className="muted">Enrolled:</span> {formatDate(en.enrolled_at)} ({en.source})</span>
              <span><span className="muted">First opened:</span> {formatDateTime(en.first_access_at)}</span>
              <span><span className="muted">Last activity:</span> {formatDateTime(en.last_access_at)}</span>
              <span><span className="muted">Last lesson:</span> {lastLesson?.title ?? '—'}</span>
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Lesson</th><th>Status</th><th className="num">Video watched</th><th>Completed</th><th>Last opened</th></tr></thead>
                <tbody>
                  {outline.chapters.map((ch) => (
                    <ChapterRows key={ch.id} title={ch.title} done={ch.completed} total={ch.required} rows={ch.lessons.map((l) => {
                      const p = byLesson.get(l.id);
                      return { id: l.id, title: l.title, required: l.is_required, status: l.status, video: l.completion_rule === 'video_pct' ? Number(p?.video_pct ?? 0) : null, completed: p?.completed_at ?? null, opened: p?.updated_at ?? null };
                    })} />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </>
  );
}

interface Row { id: string; title: string; required: boolean; status: 'todo' | 'in_progress' | 'done'; video: number | null; completed: string | null; opened: string | null }

function ChapterRows({ title, done, total, rows }: { title: string; done: number; total: number; rows: Row[] }) {
  return (
    <>
      <tr><td colSpan={5} style={{ background: 'var(--surface-sunken)', fontWeight: 600, color: 'var(--ink-strong)' }}>{title} <span className="small muted">· {done} of {total}</span></td></tr>
      {rows.map((r) => (
        <tr key={r.id}>
          <td>{r.title}{!r.required && <span className="small muted"> (optional)</span>}</td>
          <td>{r.status === 'done' ? <Badge tone="success">Done</Badge> : r.status === 'in_progress' ? <Badge tone="brand">Started</Badge> : <span className="muted">Not opened</span>}</td>
          <td className="num">{r.video === null ? '—' : `${Math.round(r.video)}%`}</td>
          <td>{formatDateTime(r.completed)}</td>
          <td>{r.opened ? timeAgo(r.opened) : '—'}</td>
        </tr>
      ))}
    </>
  );
}
